<template>
  <div class="w-full h-full flex items-center justify-center" :style="{ height: height + 'px' }">
    <div v-if="hasData" class="w-full h-full flex flex-col">
      <!-- Chart Area -->
      <div v-if="!horizontal" :class="['flex-1 flex items-end justify-around pb-4 pt-4', spacingClass]">
        <div
          v-for="(label, labelIndex) in labels"
          :key="labelIndex"
          class="flex flex-col items-center justify-end flex-1"
          style="height: 90%;"
        >
          <!-- Stacked Bars -->
          <div v-if="stacked" class="w-full flex items-end justify-center h-full">
            <div
              class="flex flex-col-reverse w-full h-full rounded-lg overflow-hidden shadow-sm"
              :style="{ maxWidth: barMaxWidth }"
            >
              <div
                v-for="(dataset, datasetIndex) in datasets"
                :key="datasetIndex"
                class="w-full transition-all duration-300 hover:opacity-90 relative group cursor-pointer"
                :class="{ 'rounded-t-lg': datasetIndex === datasets.length - 1 }"
                :style="{
                  height: `${getStackedBarSegmentPercentage(dataset.data[labelIndex], labelIndex)}%`,
                  backgroundColor: dataset.backgroundColor || colors[datasetIndex % colors.length],
                  minHeight: dataset.data[labelIndex] > 0 ? '3px' : '0'
                }"
              >
                <!-- Tooltip on hover -->
                <div :class="['absolute transform opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none whitespace-nowrap', getTooltipPosition(labelIndex)]">
                  <div class="bg-secondary-800 dark:bg-secondary-700 text-white px-3 py-1.5 rounded-lg shadow-lg text-center">
                    <p class="text-xs font-semibold">{{ dataset.label }}: {{ dataset.data[labelIndex] }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Grouped Bars -->
          <div v-else class="w-full flex items-end justify-center gap-1 h-full">
            <div
              v-for="(dataset, datasetIndex) in datasets"
              :key="datasetIndex"
              class="flex-1 rounded-t-lg transition-all duration-300 hover:opacity-80 relative group cursor-pointer"
              :style="{
                height: `${getBarHeight(dataset.data[labelIndex])}%`,
                backgroundColor: dataset.backgroundColor || colors[datasetIndex % colors.length],
                maxWidth: groupedBarMaxWidth,
              }"
            >
              <!-- Tooltip on hover -->
              <div :class="['absolute transform opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none', getTooltipPosition(labelIndex)]">
                <div class="bg-secondary-800 dark:bg-secondary-700 text-white px-3 py-2 rounded-lg shadow-lg text-center whitespace-nowrap">
                  <p class="text-xs font-semibold">{{ dataset.label }}</p>
                  <p class="text-sm font-bold">{{ dataset.data[labelIndex] }}</p>
                  <p class="text-xs text-secondary-300">{{ label }}</p>
                </div>
              </div>
            </div>
          </div>

          <div class="mt-3 text-center">
            <p class="text-xs font-medium text-secondary-700 dark:text-secondary-300 truncate max-w-[80px]" :title="label">
              {{ label }}
            </p>
          </div>
        </div>
      </div>

      <div v-else class="flex flex-col justify-around h-full space-y-3">
        <div
          v-for="(item, index) in simpleData"
          :key="index"
          class="flex items-center space-x-3"
        >
          <div class="w-32 text-right">
            <p class="text-sm font-medium text-secondary-700 dark:text-secondary-300 truncate" :title="item.label">
              {{ item.label }}
            </p>
          </div>
          <div class="flex-1 flex items-center space-x-2">
            <div
              class="h-8 rounded transition-all duration-300 hover:opacity-80 relative group"
              :style="{
                width: `${getBarWidth(item.value)}%`,
                backgroundColor: colors[index % colors.length],
              }"
            >
              <div class="absolute right-2 top-1/2 transform -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
                <span class="text-xs font-semibold text-white">{{ item.value }}</span>
              </div>
            </div>
            <span class="text-sm font-medium text-secondary-700 dark:text-secondary-300 min-w-[40px]">{{ item.value }}</span>
          </div>
        </div>
      </div>

      <!-- Legend for multiple datasets -->
      <div v-if="datasets.length > 1" class="flex justify-center gap-4 mt-4">
        <div
          v-for="(dataset, index) in datasets"
          :key="index"
          class="flex items-center gap-2"
        >
          <div
            class="w-4 h-4 rounded"
            :style="{ backgroundColor: dataset.backgroundColor || colors[index % colors.length] }"
          ></div>
          <span class="text-xs font-medium text-secondary-700 dark:text-secondary-300">{{ dataset.label }}</span>
        </div>
      </div>
    </div>
    <div v-else class="flex items-center justify-center h-full text-secondary-500 dark:text-secondary-400">
      <p class="text-sm">No data available</p>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  data: {
    type: [Array, Object],
    default: () => [],
  },
  height: {
    type: Number,
    default: 300,
  },
  colors: {
    type: Array,
    default: () => ['#3b82f6', '#10b981', '#f59e0b', '#ef4444'],
  },
  horizontal: {
    type: Boolean,
    default: false,
  },
  stacked: {
    type: Boolean,
    default: false,
  },
});

const labels = computed(() => {
  if (props.data?.labels) {
    return props.data.labels;
  }
  if (Array.isArray(props.data)) {
    return props.data.map(item => item.label || '');
  }
  return [];
});

// Dynamically adjust spacing based on number of bars
const spacingClass = computed(() => {
  const barCount = labels.value.length;
  if (barCount >= 14) return 'space-x-1'; // Very small spacing for 14+ bars
  if (barCount >= 10) return 'space-x-2'; // Small spacing for 10-13 bars
  if (barCount >= 7) return 'space-x-3';  // Medium spacing for 7-9 bars
  return 'space-x-6'; // Default large spacing for fewer bars
});

// Dynamically adjust bar max-width based on number of bars
const barMaxWidth = computed(() => {
  const barCount = labels.value.length;
  if (barCount >= 14) return '35px'; // Narrower bars for 14+ bars
  if (barCount >= 10) return '45px'; // Slightly narrower for 10-13 bars
  return '60px'; // Default width for fewer bars
});

const groupedBarMaxWidth = computed(() => {
  const barCount = labels.value.length;
  if (barCount >= 14) return '25px'; // Narrower bars for 14+ bars
  if (barCount >= 10) return '32px'; // Slightly narrower for 10-13 bars
  return '40px'; // Default width for fewer bars
});

const datasets = computed(() => {
  if (props.data?.datasets && Array.isArray(props.data.datasets)) {
    return props.data.datasets;
  }
  // Single dataset from array format
  if (Array.isArray(props.data)) {
    return [{
      label: 'Value',
      data: props.data.map(item => item.value || 0),
      backgroundColor: props.colors[0],
    }];
  }
  return [];
});

// For horizontal bars (simple data)
const simpleData = computed(() => {
  if (Array.isArray(props.data)) {
    return props.data;
  }
  if (props.data?.labels && props.data?.datasets) {
    return props.data.labels.map((label, index) => ({
      label,
      value: props.data.datasets[0]?.data[index] || 0
    }));
  }
  return [];
});

const hasData = computed(() => {
  return datasets.value.length > 0 && datasets.value.some(ds => ds.data && ds.data.some(v => v > 0));
});

const maxValue = computed(() => {
  if (props.stacked) {
    // For stacked bars, max is the sum of all dataset values for each label
    const sums = labels.value.map((_, labelIndex) => {
      return datasets.value.reduce((sum, dataset) => {
        return sum + (dataset.data[labelIndex] || 0);
      }, 0);
    });
    return Math.max(...sums, 1);
  } else {
    // For grouped bars, max is the highest individual value
    const allValues = datasets.value.flatMap(ds => ds.data || []);
    return Math.max(...allValues, 1);
  }
});

const getBarHeight = (value) => {
  if (!value || value === 0) return 0;
  return (value / maxValue.value) * 100;
};

const getStackedBarSegmentHeight = (value, labelIndex) => {
  if (!value || value === 0) return 0;
  // Calculate the total for this label
  const total = datasets.value.reduce((sum, dataset) => {
    return sum + (dataset.data[labelIndex] || 0);
  }, 0);

  // Calculate what percentage of max this segment represents
  return (value / maxValue.value) * 100;
};

// Calculate segment height as percentage of that specific bar's total (each bar is 100% height)
const getStackedBarSegmentPercentage = (value, labelIndex) => {
  if (!value || value === 0) return 0;

  // Calculate the total for THIS specific bar/label
  const total = datasets.value.reduce((sum, dataset) => {
    return sum + (dataset.data[labelIndex] || 0);
  }, 0);

  // Return the percentage of THIS bar's total
  return total > 0 ? (value / total) * 100 : 0;
};

const getBarWidth = (value) => {
  if (!value || value === 0) return 0;
  const max = Math.max(...simpleData.value.map(item => item.value || 0), 1);
  return (value / max) * 100;
};

// Determine tooltip position based on bar index
// Left half bars: tooltip on right, Right half bars: tooltip on left
const getTooltipPosition = (index) => {
  const totalBars = labels.value.length;
  const midPoint = totalBars / 2;

  if (index < midPoint) {
    // Left side bars - tooltip on right
    return 'left-full ml-2 -translate-y-1/2 top-1/2';
  } else {
    // Right side bars - tooltip on left
    return 'right-full mr-2 -translate-y-1/2 top-1/2';
  }
};
</script>
